"use client";

import { Pause, Play } from "lucide-react";
import {
  animate,
  type MotionValue,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "motion/react";
import { type PointerEvent, useEffect, useRef } from "react";
import type { PreviewStatus } from "@/components/music/use-music-preview";
import { useLowPowerMode } from "@/hooks/use-low-power-mode";
import { musicArtworkSrc } from "@/lib/music/links";
import type { ProfileMusicSong } from "@/lib/music/types";
import { useCaseDrift } from "./case-drift";
import {
  CLOSE_DURATION,
  CLOSE_POSE_DELAY,
  closeKeyframes,
} from "./case-motion";
import { MusicSticker, stickersForSong } from "./music-stickers";

/**
 * A CD jewel case in CSS 3D — after the three.js case in
 * annnguyentt/vibe-coding `cd-case`, rebuilt from flat faces so it costs a
 * handful of composited layers instead of a WebGL context per profile.
 *
 * Faces (case-local, z toward the viewer):
 *   tray front  — dark smoked tray, hub rosette, the disc
 *   tray back   — the back insert: tracklist + barcode (seen mid-flourish)
 *   lid front   — clear plastic over the cover art, stickers on top
 *   lid back    — the booklet's inside: liner notes for this song
 *
 * The lid hinges on the LEFT edge and swings toward the viewer to lie almost
 * flat beside the tray (−176°, the reference's 3.05 rad). Opening slides the
 * pair right and shrinks it so both halves fit the column, like the
 * reference's `spread`. The only frame loop is the idle drift
 * (`useCaseDrift`), which runs for the active case while it is on screen and
 * never under reduced motion or low power; the disc spin is a compositor-only
 * CSS animation that only runs while a preview is actually playing.
 */

const OPEN_LID_DEG = -176;
/**
 * The case has real thickness: the lid hinges LID_LIFT above the middle plane
 * and the tray (with the disc) sits TRAY_DEPTH below it — ~4.5% of the edge,
 * a jewel case's proportions. With the old 1.2% gap the closing lid passed
 * within ~2px of the disc at shallow angles, and the compositor's 3D depth
 * sort could paint the disc over the lid's top edge just before it landed.
 */
const LID_LIFT = 0.025;
const TRAY_DEPTH = 0.02;
/** Rest pose: enough tilt to read as an object, not enough to hide the art. */
const REST_TILT_X = 9;
const REST_TILT_Y = -12;
const OPEN_TILT_X = 16;

export interface CdCaseProps {
  song: ProfileMusicSong & { position: number };
  /** Case edge in px (square). */
  size: number;
  /** Scale applied while open so the spread pair fits the column. */
  openScale: number;
  open: boolean;
  /** One full turn on the way open — for a tap, not for a swipe. */
  flourish: boolean;
  status: PreviewStatus;
  playing: boolean;
  progress: MotionValue<number>;
  ownerFirstName: string;
  tracklist: ReadonlyArray<{ id: string; title: string; artistName: string }>;
  /** Interactive only when this is the active slide. */
  interactive: boolean;
  onOpen: () => void;
  onClose: () => void;
  onTogglePlay: () => void;
}

export function CdCase({
  song,
  size,
  openScale,
  open,
  flourish,
  status,
  playing,
  progress,
  ownerFirstName,
  tracklist,
  interactive,
  onOpen,
  onClose,
  onTogglePlay,
}: CdCaseProps) {
  const reduceMotion = useReducedMotion() ?? false;
  const calm = useLowPowerMode();
  const rootRef = useRef<HTMLDivElement>(null);
  const S = size;
  const tint = song.artworkBgColor ? `#${song.artworkBgColor}` : "#26262c";
  const ink = song.artworkTextColor ? `#${song.artworkTextColor}` : "#f4f4f5";
  const coverSrc = musicArtworkSrc(song.artworkUrl, S * 2);
  const stickers = stickersForSong(song);

  // ── Pose ────────────────────────────────────────────────────────────────
  // Hover lean (fine pointers only) on top of the rest/open pose.
  const leanX = useSpring(0, { stiffness: 160, damping: 18 });
  const leanY = useSpring(0, { stiffness: 160, damping: 18 });
  const spin = useMotionValue(0);
  const poseX = useMotionValue(open ? OPEN_TILT_X : REST_TILT_X);
  const poseY = useMotionValue(open ? 0 : REST_TILT_Y);
  // The close's landing thunk (pitch) and lean (roll), in degrees.
  const thunk = useMotionValue(0);
  const roll = useMotionValue(0);
  // Alive like the wallet stickers: a slow idle drift and, on phones, the
  // device's tilt — both small, so the case reads as an object in the hand,
  // never as wobbling UI.
  const drift = useCaseDrift({
    targetRef: rootRef,
    enabled: interactive && !reduceMotion && !calm,
  });
  const floatY = drift.floatY;
  const rotateX = useTransform(
    () => poseX.get() + leanX.get() + thunk.get() + drift.pitch.get(),
  );
  const rotateY = useTransform(
    () => poseY.get() + leanY.get() + spin.get() + drift.yaw.get(),
  );
  // The plastic's highlight band slides as the case turns.
  const sheenX = useTransform(rotateY, [-30, 30], ["-35%", "35%"]);

  // ── Lid + spread ────────────────────────────────────────────────────────
  const openX = (openScale * S) / 2;
  const lid = useMotionValue(open ? OPEN_LID_DEG : 0);
  const spreadX = useMotionValue(open ? openX : 0);
  const spreadScale = useMotionValue(open ? openScale : 1);
  const animatingRef = useRef(false);
  const wasOpen = useRef(open);

  // A resize while settled re-seats the pair without animating. Declared
  // before the transition effect, so on an open/close render `wasOpen` still
  // holds the old value — that render belongs to the animation, not to us.
  useEffect(() => {
    if (animatingRef.current || wasOpen.current !== open) return;
    spreadX.set(open ? openX : 0);
    spreadScale.set(open ? openScale : 1);
  }, [open, openX, openScale, spreadX, spreadScale]);

  useEffect(() => {
    if (wasOpen.current === open) return;
    wasOpen.current = open;

    if (reduceMotion) {
      lid.set(open ? OPEN_LID_DEG : 0);
      spreadX.set(open ? openX : 0);
      spreadScale.set(open ? openScale : 1);
      poseX.set(open ? OPEN_TILT_X : REST_TILT_X);
      poseY.set(open ? 0 : REST_TILT_Y);
      return;
    }

    animatingRef.current = true;
    const controls: Array<{ stop: () => void }> = [];
    const done = () => {
      animatingRef.current = false;
    };

    if (open) {
      // Opening: the lid swings on a soft spring; on a tap the whole case
      // also turns once (the reference's OPEN.yaw, whipped through the middle).
      const delay = flourish ? 0.38 : 0.05;
      if (flourish) {
        spin.set(0);
        controls.push(
          animate(spin, 360, {
            duration: 1.05,
            ease: [0.6, 0.02, 0.28, 1],
            onComplete: () => spin.set(0),
          }),
        );
      }
      const spring = { type: "spring" as const, stiffness: 110, damping: 20 };
      controls.push(
        animate(lid, OPEN_LID_DEG, {
          type: "spring",
          stiffness: 70,
          damping: 11,
          delay,
          onComplete: done,
        }),
        animate(spreadX, openX, { ...spring, delay: delay + 0.07 }),
        animate(spreadScale, openScale, { ...spring, delay: delay + 0.07 }),
        animate(poseX, OPEN_TILT_X, {
          type: "spring",
          stiffness: 120,
          damping: 20,
        }),
        animate(poseY, 0, { type: "spring", stiffness: 120, damping: 20 }),
      );
    } else {
      // Closing: the reference's hand-keyed close — flick, drift, land,
      // pop, settle — with the pair sliding home late and a thunk on landing.
      const frames = closeKeyframes({
        openLidDeg: lid.get(),
        openX: spreadX.get(),
        openScale: spreadScale.get(),
      });
      const keyed = (times: number[]) => ({
        duration: CLOSE_DURATION,
        times,
        ease: "linear" as const,
      });
      controls.push(
        animate(lid, frames.lid.values, {
          ...keyed(frames.lid.times),
          onComplete: done,
        }),
        animate(spreadX, frames.x.values, keyed(frames.x.times)),
        animate(spreadScale, frames.scale.values, keyed(frames.scale.times)),
        animate(thunk, frames.thunk.values, keyed(frames.thunk.times)),
        animate(roll, frames.roll.values, keyed(frames.roll.times)),
        // Back to the rest tilt only once the lid is mostly over.
        animate(poseX, REST_TILT_X, {
          type: "spring",
          stiffness: 90,
          damping: 18,
          delay: CLOSE_POSE_DELAY,
        }),
        animate(poseY, REST_TILT_Y, {
          type: "spring",
          stiffness: 90,
          damping: 18,
          delay: CLOSE_POSE_DELAY,
        }),
      );
    }
    return () => {
      for (const control of controls) control.stop();
      animatingRef.current = false;
    };
  }, [
    open,
    flourish,
    reduceMotion,
    openX,
    openScale,
    lid,
    spreadX,
    spreadScale,
    poseX,
    poseY,
    spin,
    thunk,
    roll,
  ]);

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse" || reduceMotion) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    leanY.set(nx * 10);
    leanX.set(-ny * 7);
  };
  const onPointerLeave = () => {
    leanX.set(0);
    leanY.set(0);
  };

  const radius = S * (8 / 146);
  const discSize = S * (120 / 146);

  return (
    <div
      ref={rootRef}
      className="relative select-none"
      style={{ width: S, height: S, perspective: S * 3.4 }}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      {/* The 3D containers below are NOT tap targets: each has a box of its
          own in the scene, and since the tray sits behind the middle plane
          (TRAY_DEPTH), the pose wrapper's empty box was catching every tap
          meant for the disc. Only the faces take pointer events. */}
      <motion.div
        className="pointer-events-none absolute inset-0"
        style={{
          transformStyle: "preserve-3d",
          x: spreadX,
          y: floatY,
          scale: spreadScale,
        }}
      >
        <motion.div
          className="pointer-events-none absolute inset-0"
          style={{
            transformStyle: "preserve-3d",
            rotateX,
            rotateY,
            rotateZ: roll,
          }}
        >
          {/* ── Tray back: the back insert ───────────────────────────── */}
          <div
            aria-hidden
            className="absolute inset-0 overflow-hidden"
            style={{
              borderRadius: radius,
              // Same slab plane as the tray front (−TRAY_DEPTH), seen from behind.
              transform: `rotateY(180deg) translateZ(${S * TRAY_DEPTH}px)`,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              background: `linear-gradient(160deg, ${tint} 0%, #0d0d10 120%)`,
              color: ink,
              boxShadow: `0 ${S * 0.04}px ${S * 0.09}px rgba(0,0,0,0.35)`,
            }}
          >
            <div
              className="absolute inset-y-0 left-0 bg-black/30"
              style={{ width: S * 0.07 }}
            />
            <div
              className="absolute flex flex-col"
              style={{
                inset: `${S * 0.08}px ${S * 0.08}px ${S * 0.08}px ${S * 0.13}px`,
                gap: S * 0.018,
              }}
            >
              <p
                className="leading-none font-black uppercase"
                style={{ fontSize: S * 0.058, letterSpacing: "0.02em" }}
              >
                {ownerFirstName}&rsquo;s mixtape
              </p>
              <ol
                className="flex flex-col opacity-85"
                style={{ gap: S * 0.012, marginTop: S * 0.02 }}
              >
                {tracklist.map((track, index) => (
                  <li
                    key={track.id}
                    className="truncate"
                    style={{ fontSize: S * 0.036 }}
                  >
                    {String(index + 1).padStart(2, "0")}. {track.title}
                    <span className="opacity-60"> — {track.artistName}</span>
                  </li>
                ))}
              </ol>
              <div
                className="mt-auto flex items-end justify-between"
                style={{ gap: S * 0.03 }}
              >
                <span
                  className="block bg-white"
                  style={{
                    width: S * 0.3,
                    height: S * 0.11,
                    padding: S * 0.01,
                    backgroundImage:
                      "repeating-linear-gradient(90deg, #111 0 2px, transparent 2px 3px, #111 3px 4px, transparent 4px 7px, #111 7px 10px, transparent 10px 11px)",
                    backgroundClip: "content-box",
                  }}
                />
                <span
                  className="text-right opacity-70"
                  style={{ fontSize: S * 0.03 }}
                >
                  Previews via
                  <br />
                  Apple Music
                </span>
              </div>
            </div>
          </div>

          {/* ── Tray front ───────────────────────────────────────────── */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: decorative surface; the disc and lid are the real buttons, and closing is also on the play bar. */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: as above. */}
          <div
            className="pointer-events-auto absolute inset-0 overflow-hidden"
            onClick={interactive && open ? onClose : undefined}
            style={{
              borderRadius: radius,
              // The tray (and the disc lying in it) sits BELOW the middle
              // plane; see LID_LIFT.
              transform: `translateZ(${-S * TRAY_DEPTH}px)`,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              background:
                "linear-gradient(150deg, #2c2d32 0%, #17181b 52%, #0b0c0e 100%)",
              boxShadow: `0 ${S * 0.04}px ${S * 0.09}px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.10)`,
            }}
          >
            {/* Perimeter rail */}
            <div
              className="absolute"
              style={{
                inset: S * 0.026,
                borderRadius: radius * 0.8,
                boxShadow:
                  "inset 0 0 0 1px rgba(255,255,255,0.07), inset 0 2px 6px rgba(0,0,0,0.6)",
              }}
            />
            {/* Disc well */}
            <div
              className="absolute top-1/2 left-1/2 rounded-full"
              style={{
                width: S * (123 / 146),
                height: S * (123 / 146),
                transform: "translate(-50%, -50%)",
                boxShadow:
                  "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 3px 10px rgba(0,0,0,0.55)",
              }}
            />
            {/* Hub rosette (shows through the disc's hole) */}
            <div
              className="absolute top-1/2 left-1/2 rounded-full"
              style={{
                width: S * (38.8 / 146),
                height: S * (38.8 / 146),
                transform: "translate(-50%, -50%)",
                background:
                  "repeating-radial-gradient(circle, rgba(255,255,255,0.14) 0 1px, transparent 1px 4px)",
              }}
            />

            <Disc
              song={song}
              coverSrc={coverSrc}
              size={discSize}
              caseSize={S}
              spinning={playing && !reduceMotion}
              status={status}
              progress={progress}
              interactive={interactive && open}
              onTogglePlay={onTogglePlay}
            />
          </div>

          {/* ── Lid ──────────────────────────────────────────────────── */}
          <motion.div
            className="pointer-events-none absolute inset-0"
            style={{
              transformStyle: "preserve-3d",
              transformOrigin: "0% 50%",
              z: S * LID_LIFT,
              rotateY: lid,
            }}
          >
            {/* Lid front: cover art under clear plastic */}
            <button
              type="button"
              tabIndex={interactive && !open ? 0 : -1}
              aria-label={`Open the case for ${song.title} by ${song.artistName}`}
              onClick={interactive && !open ? onOpen : undefined}
              className="pointer-events-auto absolute inset-0 block cursor-pointer overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-brand"
              style={{
                borderRadius: radius,
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                background: tint,
                boxShadow: `0 ${S * 0.04}px ${S * 0.09}px rgba(0,0,0,0.35)`,
              }}
            >
              <CoverInsert
                coverSrc={coverSrc}
                song={song}
                caseSize={S}
                tint={tint}
                ink={ink}
              />
              <Plastic caseSize={S} radius={radius} sheenX={sheenX} />
              {stickers.map((sticker, index) => (
                <MusicSticker
                  // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, deterministic layout per song.
                  key={index}
                  sticker={sticker}
                  size={S}
                />
              ))}
            </button>

            {/* Lid back: the booklet's inside — liner notes */}
            <button
              type="button"
              tabIndex={interactive && open ? 0 : -1}
              aria-label="Close the case"
              onClick={interactive && open ? onClose : undefined}
              className="pointer-events-auto absolute inset-0 block cursor-pointer overflow-hidden text-left outline-none focus-visible:ring-2 focus-visible:ring-brand"
              style={{
                borderRadius: radius,
                transform: "rotateY(180deg)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                background: `linear-gradient(170deg, ${tint} 0%, color-mix(in oklab, ${tint} 55%, #000) 100%)`,
                color: ink,
              }}
            >
              <LinerNotes
                song={song}
                caseSize={S}
                ownerFirstName={ownerFirstName}
              />
              <Plastic caseSize={S} radius={radius} sheenX={sheenX} mirrored />
            </button>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

function CoverInsert({
  coverSrc,
  song,
  caseSize: S,
  tint,
  ink,
}: {
  coverSrc: string | null;
  song: ProfileMusicSong;
  caseSize: number;
  tint: string;
  ink: string;
}) {
  return (
    <>
      <span
        className="absolute overflow-hidden"
        style={{
          top: S * 0.024,
          bottom: S * 0.024,
          right: S * 0.024,
          left: S * 0.07,
          borderRadius: S * 0.012,
          background: tint,
        }}
      >
        {coverSrc ? (
          // biome-ignore lint/performance/noImgElement: Apple's CDN serves the exact size requested; next/image adds nothing (images are unoptimized app-wide).
          <img
            src={coverSrc}
            alt=""
            draggable={false}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            className="flex h-full w-full flex-col justify-end leading-tight font-black"
            style={{ color: ink, padding: S * 0.06, fontSize: S * 0.09 }}
          >
            {song.title}
          </span>
        )}
      </span>
      {/* Hinge spine: ribbed clear plastic */}
      <span
        className="absolute inset-y-0 left-0"
        style={{
          width: S * 0.062,
          background:
            "repeating-linear-gradient(180deg, rgba(255,255,255,0.20) 0 1px, rgba(255,255,255,0.04) 1px 3px), linear-gradient(90deg, rgba(255,255,255,0.20), rgba(255,255,255,0.06))",
          boxShadow: "inset -1px 0 0 rgba(255,255,255,0.35)",
        }}
      />
    </>
  );
}

function Plastic({
  caseSize: S,
  radius,
  sheenX,
  mirrored = false,
}: {
  caseSize: number;
  radius: number;
  sheenX: MotionValue<string>;
  mirrored?: boolean;
}) {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          borderRadius: radius,
          background: mirrored
            ? "linear-gradient(235deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0) 52%, rgba(255,255,255,0.10) 100%)"
            : "linear-gradient(125deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.06) 26%, rgba(255,255,255,0) 50%, rgba(255,255,255,0.07) 76%, rgba(255,255,255,0.22) 100%)",
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.55), inset 0 0 0 ${Math.max(2, S * 0.012)}px rgba(255,255,255,0.08), inset 0 1px 1px rgba(255,255,255,0.8), inset 0 -2px 6px rgba(0,0,0,0.18)`,
        }}
      />
      {/* Inner ridge, where the lid's lip sits */}
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          inset: S * 0.022,
          borderRadius: radius * 0.75,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.16)",
        }}
      />
      <motion.span
        aria-hidden
        className="pointer-events-none absolute -inset-x-1/2 inset-y-0"
        style={{
          x: sheenX,
          background:
            "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.30) 47%, rgba(255,255,255,0.06) 53%, transparent 60%)",
          mixBlendMode: "screen",
        }}
      />
    </>
  );
}

function LinerNotes({
  song,
  caseSize: S,
  ownerFirstName,
}: {
  song: ProfileMusicSong & { position: number };
  caseSize: number;
  ownerFirstName: string;
}) {
  return (
    <span
      className="absolute flex flex-col"
      style={{
        inset: `${S * 0.08}px ${S * 0.09}px ${S * 0.08}px ${S * 0.09}px`,
      }}
    >
      <span
        className="font-bold uppercase opacity-70"
        style={{ fontSize: S * 0.036, letterSpacing: "0.14em" }}
      >
        Side A · Track {String(song.position + 1).padStart(2, "0")}
      </span>
      <span
        className="line-clamp-3 leading-[0.95] font-black tracking-tight"
        style={{ fontSize: S * 0.105, marginTop: S * 0.05 }}
      >
        {song.title}
      </span>
      <span
        className="truncate font-semibold opacity-90"
        style={{ fontSize: S * 0.056, marginTop: S * 0.03 }}
      >
        {song.artistName}
      </span>
      {song.albumName ? (
        <span
          className="truncate opacity-60"
          style={{ fontSize: S * 0.042, marginTop: S * 0.012 }}
        >
          {song.albumName}
        </span>
      ) : null}
      {/* Ruled lines, like the back of a booklet page */}
      <span
        aria-hidden
        className="mt-auto block opacity-25"
        style={{
          height: S * 0.13,
          backgroundImage:
            "repeating-linear-gradient(180deg, currentColor 0 1px, transparent 1px 12px)",
          backgroundSize: `100% ${S * 0.043}px`,
        }}
      />
      <span
        className="font-semibold uppercase opacity-60"
        style={{
          fontSize: S * 0.03,
          letterSpacing: "0.12em",
          marginTop: S * 0.02,
        }}
      >
        Picked by {ownerFirstName}
      </span>
    </span>
  );
}

// ── Disc ─────────────────────────────────────────────────────────────────────

/** Annulus masks: printed label vs. the bare data rim around it. */
const LABEL_MASK =
  "radial-gradient(circle at center, transparent 0 24.5%, #000 25% 86.5%, transparent 87%)";
const RIM_MASK =
  "radial-gradient(circle at center, transparent 0 86%, #000 86.5% 98.6%, transparent 99%)";

// Diffraction lobes. They do NOT spin with the disc: the grating's colour
// stays put relative to the light, which is what makes a real CD shimmer.
const DIFFRACTION =
  "conic-gradient(from 300deg, transparent 0deg, rgba(255,90,190,0.75) 18deg, rgba(255,226,92,0.75) 26deg, rgba(92,255,206,0.75) 34deg, rgba(96,140,255,0.75) 42deg, transparent 56deg, transparent 180deg, rgba(255,90,190,0.75) 198deg, rgba(255,226,92,0.75) 206deg, rgba(92,255,206,0.75) 214deg, rgba(96,140,255,0.75) 222deg, transparent 236deg)";

const METAL =
  "conic-gradient(from 0deg, #c3c8d0, #f4f6f8 12%, #b4bac3 25%, #eef1f4 38%, #c0c5cd 50%, #f6f7f9 62%, #b8bec7 75%, #eceff2 88%, #c3c8d0)";

function Disc({
  song,
  coverSrc,
  size: D,
  caseSize: S,
  spinning,
  status,
  progress,
  interactive,
  onTogglePlay,
}: {
  song: ProfileMusicSong & { position: number };
  coverSrc: string | null;
  size: number;
  caseSize: number;
  spinning: boolean;
  status: PreviewStatus;
  progress: MotionValue<number>;
  interactive: boolean;
  onTogglePlay: () => void;
}) {
  const showPause = status === "playing" || status === "loading";
  const Glyph = showPause ? Pause : Play;
  const unavailable = !song.previewUrl;

  return (
    <button
      type="button"
      tabIndex={interactive ? 0 : -1}
      disabled={unavailable}
      aria-label={
        unavailable
          ? "No preview available for this song"
          : showPause
            ? `Pause ${song.title}`
            : `Play a preview of ${song.title}`
      }
      onClick={(event) => {
        // The tray behind closes the case; the disc must not.
        event.stopPropagation();
        if (interactive) onTogglePlay();
      }}
      className="absolute top-1/2 left-1/2 cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-default"
      style={{
        width: D,
        height: D,
        transform: "translate(-50%, -50%)",
        boxShadow: `0 ${S * 0.012}px ${S * 0.04}px rgba(0,0,0,0.55)`,
      }}
    >
      {/* Spinning layers: metal, printed label, a sticker on the print */}
      <span
        className="absolute inset-0 animate-spin rounded-full"
        style={{
          animationDuration: "3.6s",
          animationPlayState: spinning ? "running" : "paused",
          background: METAL,
        }}
      >
        <span
          className="absolute inset-0 rounded-full"
          style={{
            maskImage: LABEL_MASK,
            WebkitMaskImage: LABEL_MASK,
            background: song.artworkBgColor
              ? `#${song.artworkBgColor}`
              : "#2a2a30",
          }}
        >
          {coverSrc ? (
            // biome-ignore lint/performance/noImgElement: same CDN image as the cover; already in cache.
            <img
              src={coverSrc}
              alt=""
              draggable={false}
              loading="lazy"
              decoding="async"
              className="h-full w-full rounded-full object-cover"
            />
          ) : null}
        </span>
        <MusicSticker
          size={D}
          sticker={{
            spec: {
              kind: "burst",
              icon: "star",
              fill: "#ffe14d",
              ink: "#1b1b1f",
            },
            x: 74,
            y: 27,
            rotate: 18,
            scale: 0.55,
          }}
        />
      </span>

      {/* Static: diffraction on the bare rim, gloss over everything */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          maskImage: RIM_MASK,
          WebkitMaskImage: RIM_MASK,
          background: DIFFRACTION,
          mixBlendMode: "screen",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(70% 60% at 30% 22%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 60%), radial-gradient(circle at center, transparent 0 86.2%, rgba(0,0,0,0.25) 86.6%, transparent 87.4%)",
        }}
      />

      {/* Clear hub + centre hole */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 rounded-full"
        style={{
          width: D * 0.5,
          height: D * 0.5,
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle, rgba(20,21,24,1) 0 23%, rgba(255,255,255,0.22) 24% 27%, rgba(255,255,255,0.10) 28% 96%, rgba(255,255,255,0.35) 98%, transparent 100%)",
          backdropFilter: "blur(1px)",
        }}
      />
      {interactive && !unavailable ? (
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-1/2 flex items-center justify-center rounded-full bg-white/90 text-neutral-900 shadow-md"
          style={{
            width: D * 0.2,
            height: D * 0.2,
            transform: "translate(-50%, -50%)",
          }}
        >
          <Glyph
            fill="currentColor"
            strokeWidth={0}
            style={{
              width: D * 0.09,
              height: D * 0.09,
              marginLeft: showPause ? 0 : D * 0.008,
            }}
          />
        </span>
      ) : null}

      {/* Preview progress around the rim */}
      <svg
        aria-hidden
        viewBox="0 0 100 100"
        className="pointer-events-none absolute -inset-[3%] -rotate-90"
        style={{ width: "106%", height: "106%" }}
      >
        <motion.circle
          cx="50"
          cy="50"
          r="48.5"
          fill="none"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth="1.1"
          strokeLinecap="round"
          style={{ pathLength: progress, opacity: showPause ? 1 : 0.4 }}
        />
      </svg>
    </button>
  );
}
